/**
 * components/ui/AppDialog.js
 * ─────────────────────────────────────────────────────────────────────────────
 * The dashboard's one dialog, and the confirmation built on it.
 *
 * Every hand-rolled overlay in the console (the team pages each had their own
 * `ModalShell` / `DialogModal`, with different radii, shadows, header styles
 * and close buttons) becomes this. Bootstrap `<Modal>`s are themed to match in
 * assets/css/lusuku-overrides.css, so old and new dialogs read as one family.
 *
 *   <AppDialog
 *     open={editing}
 *     title="Edit team member"
 *     subtitle="jane@shop.ug"
 *     onClose={() => setEditing(false)}
 *     busy={saving}
 *     footer={<>
 *       <AppButton variant="quiet" onClick={...}>Cancel</AppButton>
 *       <AppButton loading={saving} onClick={save}>Save changes</AppButton>
 *     </>}
 *   >
 *     ...fields...
 *   </AppDialog>
 *
 * Behaviour that comes free: Esc closes (only the top-most dialog, and never
 * while a dropdown inside it is open), a click on the backdrop closes,
 * focus moves into the dialog and returns to whatever opened it, Tab stays
 * inside, and the page behind stops scrolling. While `busy`, nothing closes
 * it, so a save in flight cannot be abandoned half way.
 */
import React, { useEffect, useLayoutEffect, useRef } from "react";
import ReactDOM from "react-dom";
import AppButton from "./AppButton.js";
import "./ui.css";

let idSeq = 0;
const stack = [];
let scrollLocks = 0;
let savedOverflow = "";

const FOCUSABLE_PARTS = [
  "a[href]", "button:not([disabled])", "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])", "textarea:not([disabled])", "[tabindex]:not([tabindex='-1'])",
];
const FOCUSABLE = FOCUSABLE_PARTS.join(",");
// The first control in the BODY, not the header's close button.
const BODY_FOCUSABLE = FOCUSABLE_PARTS.map((p) => `.lk-dialog__body ${p}`).join(",");

const lockScroll = () => {
  if (scrollLocks++ === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
};
const unlockScroll = () => {
  if (--scrollLocks <= 0) {
    scrollLocks = 0;
    document.body.style.overflow = savedOverflow;
  }
};

const CloseIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const AppDialog = ({
  open,
  show,
  onClose,
  title,
  subtitle,
  size = "md",
  busy = false,
  closeOnBackdrop = true,
  hideClose = false,
  footer,
  children,
  initialFocusRef,
  className,
  bodyClassName,
  "aria-label": ariaLabel,
}) => {
  const isOpen = Boolean(open ?? show);
  const panelRef = useRef(null);
  const idRef = useRef(null);
  if (!idRef.current) idRef.current = `lk-dialog-${++idSeq}`;
  const titleId = `${idRef.current}-title`;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  // Stack, scroll lock, focus in, focus back.
  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const token = idRef.current;
    const opener = document.activeElement;
    stack.push(token);
    lockScroll();
    const panel = panelRef.current;
    const target =
      (initialFocusRef && initialFocusRef.current) ||
      (panel && panel.querySelector("[data-autofocus]")) ||
      (panel && panel.querySelector(BODY_FOCUSABLE)) ||
      panel;
    if (target && target.focus) target.focus();
    return () => {
      const at = stack.lastIndexOf(token);
      if (at >= 0) stack.splice(at, 1);
      unlockScroll();
      if (opener && opener.focus && document.contains(opener)) opener.focus();
    };
  }, [isOpen, initialFocusRef]);

  // Esc: only the top-most dialog, and not when something inside already
  // handled the key (an open dropdown stops it).
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (stack[stack.length - 1] !== idRef.current) return;
      if (busyRef.current || !closeRef.current) return;
      e.preventDefault();
      closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen]);

  if (!isOpen) return null;

  const trapTab = (e) => {
    if (e.key !== "Tab" || !panelRef.current) return;
    const nodes = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter((n) => n.tabIndex >= 0 && (n.offsetParent !== null || n === document.activeElement));
    if (!nodes.length) { e.preventDefault(); return; }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const onBackdrop = (e) => {
    if (e.target !== e.currentTarget) return;
    if (!closeOnBackdrop || busy || !onClose) return;
    onClose();
  };

  return ReactDOM.createPortal(
    <div className="lk-dialog__overlay" onMouseDown={onBackdrop}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
        className={["lk-dialog", `lk-dialog--${size}`, className].filter(Boolean).join(" ")}
        onKeyDown={trapTab}
      >
        {(title || (onClose && !hideClose)) && (
          <div className="lk-dialog__header">
            <div>
              {title ? <h2 id={titleId} className="lk-dialog__title">{title}</h2> : null}
              {subtitle ? <p className="lk-dialog__subtitle">{subtitle}</p> : null}
            </div>
            {onClose && !hideClose ? (
              <button type="button" className="lk-dialog__close" aria-label="Close" onClick={onClose} disabled={busy}>
                <CloseIcon />
              </button>
            ) : null}
          </div>
        )}
        <div className={["lk-dialog__body", bodyClassName].filter(Boolean).join(" ")}>{children}</div>
        {footer ? <div className="lk-dialog__footer">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
};

/**
 * The replacement for `window.confirm`: a consequence, not "are you sure".
 * `tone` is the confirm button: danger (red), warning, or primary (navy).
 */
export const AppConfirmDialog = ({
  open,
  show,
  title,
  consequence,
  body,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "danger",
  busy = false,
  onConfirm,
  onClose,
  onCancel,
}) => {
  const cancel = onClose || onCancel;
  return (
    <AppDialog
      open={open ?? show}
      title={title}
      size="sm"
      busy={busy}
      onClose={cancel}
      footer={
        <>
          {/* Focus starts on the safe choice for anything destructive, so a
              stray Enter cancels rather than deletes. */}
          <AppButton variant="quiet" onClick={cancel} disabled={busy} data-autofocus={tone !== "primary" || undefined}>
            {cancelLabel}
          </AppButton>
          <AppButton
            variant={tone === "primary" ? "primary" : tone === "warning" ? "warning" : "danger-solid"}
            onClick={onConfirm}
            loading={busy}
            data-autofocus={tone === "primary" || undefined}
          >
            {confirmLabel}
          </AppButton>
        </>
      }
    >
      {consequence || body ? <p style={{ margin: 0, color: "#475569" }}>{consequence || body}</p> : null}
      {children}
    </AppDialog>
  );
};

export default AppDialog;
